import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { FishBoneAnalysis } from '../types/project-form';

interface FishBoneDiagramProps {
  problemStatement: string;
  projectTitle: string;
  fishBoneAnalysis?: FishBoneAnalysis;
}

const FishBoneDiagram: React.FC<FishBoneDiagramProps> = ({ problemStatement, projectTitle, fishBoneAnalysis }) => {
  // SVG dimensions and positioning
  const svgWidth = 900;
  const svgHeight = 500;
  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;
  const spineLength = svgWidth * 0.7;
  const boneLength = 150;
  const spineStartX = centerX - spineLength / 2;
  const spineEndX = centerX + spineLength / 2;
  
  // Function to split text into lines with max length
  const splitText = (text: string, maxLength: number = 25): string[] => {
    if (!text) return [];
    
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + ' ' + word).length <= maxLength) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  };
  
  // Process causes from each category - support both individual cause items and legacy comma-separated strings
  const extractCauses = (category: any): string[] => {
    if (!category) return [];
    
    // First check if we have causeItems array (new format)
    if (category.causeItems && category.causeItems.length > 0) {
      return category.causeItems.map((cause: any) => cause.text.trim()).filter((text: string) => text.length > 0);
    }
    
    // Fall back to legacy comma-separated causes string
    if (!category.causes) return [];
    
    // Split by newlines or commas
    const causes = category.causes.split(/[\n,]+/).filter((cause: string) => cause.trim().length > 0);
    return causes.map((cause: string) => cause.trim());
  };

  // Get causes for each category
  const peopleCauses = extractCauses(fishBoneAnalysis?.peopleCauses);
  const processCausesList = extractCauses(fishBoneAnalysis?.processCauses);
  const policyCauses = extractCauses(fishBoneAnalysis?.policyCauses);
  const placeCauses = extractCauses(fishBoneAnalysis?.placeCauses);
  const provisionCauses = extractCauses(fishBoneAnalysis?.provisionCauses);
  
  // Better spacing for cause bones along the spine
  const spineSegment = spineLength / 6; // Divide spine into 6 segments for better spacing
  
  // Category angles for bones (in degrees from horizontal) - flipped to point right
  const categoryAngles = {
    people: 150,     // Down-left (was 30 down-right)
    process: -150,   // Up-left (was -30 up-right)
    policy: 150,     // Down-left (was 30 down-right)
    place: -150,     // Up-left (was -30 up-right)
    provision: 150,  // Down-left (was 30 down-right)
  };

  // Function to calculate end point of a line given start, length and angle
  const calculateEndPoint = (startX: number, startY: number, length: number, angleDegrees: number) => {
    const angleRadians = (angleDegrees * Math.PI) / 180;
    const endX = startX + length * Math.cos(angleRadians);
    const endY = startY + length * Math.sin(angleRadians);
    return { x: endX, y: endY };
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4, overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Fish Bone Analysis
      </Typography>
      
      <Box sx={{ width: '100%', overflow: 'auto' }}>
        <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
          {/* Main spine */}
          <line 
            x1={spineStartX} 
            y1={centerY} 
            x2={spineEndX} 
            y2={centerY} 
            stroke="black" 
            strokeWidth="2"
          />
          
          {/* QI Project Title (fish head) - now on the right side with sharp edge pointing forward */}
          <g transform={`translate(${spineEndX}, ${centerY})`}>
            <polygon 
              points={`120,0 0,120 0,-120`} 
              fill="#f5f5f5" 
              stroke="black" 
              strokeWidth="2"
            />
            {splitText(projectTitle, 15).map((line, i) => (
              <text
                key={i}
                x="70"
                y={i * 15 - 15 * (splitText(projectTitle, 15).length - 1) / 2}
                fontSize="12"
                textAnchor="start"
                dominantBaseline="middle"
              >
                {line}
              </text>
            ))}
          </g>

          {/* People bone (far right, down) */}
          {fishBoneAnalysis?.peopleCauses && (
            <>
              {/* Main bone */}
              <g transform={`translate(${spineStartX + spineSegment * 5}, ${centerY})`}>
                <line 
                  x1="0" 
                  y1="0" 
                  x2={boneLength * Math.cos(categoryAngles.people * Math.PI / 180)} 
                  y2={boneLength * Math.sin(categoryAngles.people * Math.PI / 180)} 
                  stroke="black" 
                  strokeWidth="1.5"
                />
                <text 
                  x={boneLength * 0.5 * Math.cos(categoryAngles.people * Math.PI / 180)}
                  y={boneLength * 0.5 * Math.sin(categoryAngles.people * Math.PI / 180) - 10}
                  fontSize="14"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  People
                </text>
                
                {/* Causes */}
                {peopleCauses.map((cause: string, index: number) => {
                  const baseX = boneLength * 0.7 * Math.cos(categoryAngles.people * Math.PI / 180);
                  const baseY = boneLength * 0.7 * Math.sin(categoryAngles.people * Math.PI / 180);
                  const offset = 20 * (index - (peopleCauses.length - 1) / 2);
                  const perpAngle = categoryAngles.people - 90;
                  
                  return (
                    <g key={`people-${index}`}>
                      <line 
                        x1={baseX + offset * Math.cos(perpAngle * Math.PI / 180)} 
                        y1={baseY + offset * Math.sin(perpAngle * Math.PI / 180)}
                        x2={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 40 * Math.cos(categoryAngles.people * Math.PI / 180)}
                        y2={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 40 * Math.sin(categoryAngles.people * Math.PI / 180)}
                        stroke="black" 
                        strokeWidth="1"
                      />
                      <text
                        x={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 50 * Math.cos(categoryAngles.people * Math.PI / 180)}
                        y={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 50 * Math.sin(categoryAngles.people * Math.PI / 180)}
                        fontSize="10"
                        textAnchor="start"
                      >
                        {cause.length > 20 ? cause.substring(0, 20) + '...' : cause}
                      </text>
                    </g>
                  );
                })}
              </g>
            </>
          )}
          
          {/* Process bone (right-center, up) */}
          {fishBoneAnalysis?.processCauses && (
            <>
              {/* Main bone */}
              <g transform={`translate(${spineStartX + spineSegment * 4}, ${centerY})`}>
                <line 
                  x1="0" 
                  y1="0" 
                  x2={boneLength * Math.cos(categoryAngles.process * Math.PI / 180)} 
                  y2={boneLength * Math.sin(categoryAngles.process * Math.PI / 180)} 
                  stroke="black" 
                  strokeWidth="1.5"
                />
                <text 
                  x={boneLength * 0.5 * Math.cos(categoryAngles.process * Math.PI / 180)}
                  y={boneLength * 0.5 * Math.sin(categoryAngles.process * Math.PI / 180) - 10}
                  fontSize="14"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  Process
                </text>
                
                {/* Causes */}
                {processCausesList.map((cause: string, index: number) => {
                  const baseX = boneLength * 0.7 * Math.cos(categoryAngles.process * Math.PI / 180);
                  const baseY = boneLength * 0.7 * Math.sin(categoryAngles.process * Math.PI / 180);
                  const offset = 20 * (index - (processCausesList.length - 1) / 2);
                  const perpAngle = categoryAngles.process - 90;
                  
                  return (
                    <g key={`process-${index}`}>
                      <line 
                        x1={baseX + offset * Math.cos(perpAngle * Math.PI / 180)} 
                        y1={baseY + offset * Math.sin(perpAngle * Math.PI / 180)}
                        x2={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 40 * Math.cos(categoryAngles.process * Math.PI / 180)}
                        y2={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 40 * Math.sin(categoryAngles.process * Math.PI / 180)}
                        stroke="black" 
                        strokeWidth="1"
                      />
                      <text
                        x={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 50 * Math.cos(categoryAngles.process * Math.PI / 180)}
                        y={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 50 * Math.sin(categoryAngles.process * Math.PI / 180)}
                        fontSize="10"
                        textAnchor="start"
                      >
                        {cause.length > 20 ? cause.substring(0, 20) + '...' : cause}
                      </text>
                    </g>
                  );
                })}
              </g>
            </>
          )}
          
          {/* Policy bone (center, down) */}
          {fishBoneAnalysis?.policyCauses && (
            <>
              {/* Main bone */}
              <g transform={`translate(${spineStartX + spineSegment * 3}, ${centerY})`}>
                <line 
                  x1="0" 
                  y1="0" 
                  x2={boneLength * Math.cos(categoryAngles.policy * Math.PI / 180)} 
                  y2={boneLength * Math.sin(categoryAngles.policy * Math.PI / 180)} 
                  stroke="black" 
                  strokeWidth="1.5"
                />
                <text 
                  x={boneLength * 0.5 * Math.cos(categoryAngles.policy * Math.PI / 180)}
                  y={boneLength * 0.5 * Math.sin(categoryAngles.policy * Math.PI / 180) - 10}
                  fontSize="14"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  Policy
                </text>
                
                {/* Causes */}
                {policyCauses.map((cause: string, index: number) => {
                  const baseX = boneLength * 0.7 * Math.cos(categoryAngles.policy * Math.PI / 180);
                  const baseY = boneLength * 0.7 * Math.sin(categoryAngles.policy * Math.PI / 180);
                  const offset = 20 * (index - (policyCauses.length - 1) / 2);
                  const perpAngle = categoryAngles.policy - 90;
                  
                  return (
                    <g key={`policy-${index}`}>
                      <line 
                        x1={baseX + offset * Math.cos(perpAngle * Math.PI / 180)} 
                        y1={baseY + offset * Math.sin(perpAngle * Math.PI / 180)}
                        x2={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 40 * Math.cos(categoryAngles.policy * Math.PI / 180)}
                        y2={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 40 * Math.sin(categoryAngles.policy * Math.PI / 180)}
                        stroke="black" 
                        strokeWidth="1"
                      />
                      <text
                        x={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 50 * Math.cos(categoryAngles.policy * Math.PI / 180)}
                        y={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 50 * Math.sin(categoryAngles.policy * Math.PI / 180)}
                        fontSize="10"
                        textAnchor="start"
                      >
                        {cause.length > 20 ? cause.substring(0, 20) + '...' : cause}
                      </text>
                    </g>
                  );
                })}
              </g>
            </>
          )}
          
          {/* Place bone (left-center, up) */}
          {fishBoneAnalysis?.placeCauses && (
            <>
              {/* Main bone */}
              <g transform={`translate(${spineStartX + spineSegment * 2}, ${centerY})`}>
                <line 
                  x1="0" 
                  y1="0" 
                  x2={boneLength * Math.cos(categoryAngles.place * Math.PI / 180)} 
                  y2={boneLength * Math.sin(categoryAngles.place * Math.PI / 180)} 
                  stroke="black" 
                  strokeWidth="1.5"
                />
                <text 
                  x={boneLength * 0.5 * Math.cos(categoryAngles.place * Math.PI / 180)}
                  y={boneLength * 0.5 * Math.sin(categoryAngles.place * Math.PI / 180) - 10}
                  fontSize="14"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  Place
                </text>
                
                {/* Causes */}
                {placeCauses.map((cause: string, index: number) => {
                  const baseX = boneLength * 0.7 * Math.cos(categoryAngles.place * Math.PI / 180);
                  const baseY = boneLength * 0.7 * Math.sin(categoryAngles.place * Math.PI / 180);
                  const offset = 20 * (index - (placeCauses.length - 1) / 2);
                  const perpAngle = categoryAngles.place - 90;
                  
                  return (
                    <g key={`place-${index}`}>
                      <line 
                        x1={baseX + offset * Math.cos(perpAngle * Math.PI / 180)} 
                        y1={baseY + offset * Math.sin(perpAngle * Math.PI / 180)}
                        x2={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 40 * Math.cos(categoryAngles.place * Math.PI / 180)}
                        y2={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 40 * Math.sin(categoryAngles.place * Math.PI / 180)}
                        stroke="black" 
                        strokeWidth="1"
                      />
                      <text
                        x={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 50 * Math.cos(categoryAngles.place * Math.PI / 180)}
                        y={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 50 * Math.sin(categoryAngles.place * Math.PI / 180)}
                        fontSize="10"
                        textAnchor="start"
                      >
                        {cause.length > 20 ? cause.substring(0, 20) + '...' : cause}
                      </text>
                    </g>
                  );
                })}
              </g>
            </>
          )}
          
          {/* Provision causes (left, down) */}
          {fishBoneAnalysis?.provisionCauses && (
            <>
              {/* Main bone */}
              <g transform={`translate(${spineStartX + spineSegment * 1}, ${centerY})`}>
                <line 
                  x1="0" 
                  y1="0" 
                  x2={boneLength * Math.cos(categoryAngles.provision * Math.PI / 180)} 
                  y2={boneLength * Math.sin(categoryAngles.provision * Math.PI / 180)} 
                  stroke="black" 
                  strokeWidth="1.5"
                />
                <text 
                  x={boneLength * Math.cos(categoryAngles.provision * Math.PI / 180) / 2}
                  y={boneLength * Math.sin(categoryAngles.provision * Math.PI / 180) / 2 - 10}
                  fontSize="14"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  Provision
                </text>
                
                {/* Causes */}
                {provisionCauses.map((cause: string, index: number) => {
                  const baseX = boneLength * 0.7 * Math.cos(categoryAngles.provision * Math.PI / 180);
                  const baseY = boneLength * 0.7 * Math.sin(categoryAngles.provision * Math.PI / 180);
                  const offset = 20 * (index - (provisionCauses.length - 1) / 2);
                  const perpAngle = categoryAngles.provision - 90;
                  
                  return (
                    <g key={`provision-${index}`}>
                      <line 
                        x1={baseX + offset * Math.cos(perpAngle * Math.PI / 180)}
                        y1={baseY + offset * Math.sin(perpAngle * Math.PI / 180)}
                        x2={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 40 * Math.cos(categoryAngles.provision * Math.PI / 180)}
                        y2={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 40 * Math.sin(categoryAngles.provision * Math.PI / 180)}
                        stroke="black" 
                        strokeWidth="1"
                      />
                      <text
                        x={baseX + offset * Math.cos(perpAngle * Math.PI / 180) + 50 * Math.cos(categoryAngles.provision * Math.PI / 180)}
                        y={baseY + offset * Math.sin(perpAngle * Math.PI / 180) + 50 * Math.sin(categoryAngles.provision * Math.PI / 180)}
                        fontSize="10"
                        textAnchor="start" 
                        dominantBaseline="middle"
                      >
                        {cause.length > 20 ? cause.substring(0, 20) + '...' : cause}
                      </text>
                    </g>
                  );
                })}
              </g>
            </>
          )}
        </svg>
      </Box>
    </Paper>
  );
};

export default FishBoneDiagram;
